import { PropsWithChildren, ReactNode, useEffect, useState } from "react";

import { useTranslation } from "next-i18next";

import { Cross } from "@/assets/icons/24/Cross";
import { PopoverContentProps } from "@radix-ui/react-popover";
import {
  Arrow,
  Content,
  Portal,
  Provider,
  Root,
  Trigger,
} from "@radix-ui/react-tooltip";

type TooltipProps = {
  defaultOpen?: boolean;
  open?: boolean;
  side?: PopoverContentProps["side"];
  sideOffset?: number;
  align?: PopoverContentProps["align"];
  alignOffset?: number;
  autoCloseAfter?: number;
  closeable?: boolean;
  content: ReactNode;
  /**
   * Fires whenever the tooltip opens or closes, including from hover and focus.
   * Radix already waits out `delayDuration` before opening, so a pointer
   * crossing the trigger does not count as a read.
   */
  onOpenChange?: (open: boolean) => void;
};

export const Tooltip = ({
  open,
  side,
  sideOffset,
  align,
  alignOffset,
  autoCloseAfter,
  closeable,
  content,
  onOpenChange,
  children,
}: PropsWithChildren<TooltipProps>) => {
  const { t } = useTranslation();
  const [openInternal, setOpenInternal] = useState(open);

  /** Update internal state whenever the prop changes. */
  useEffect(() => {
    setOpenInternal(open);
  }, [open]);

  /** If `autoCloseAfter` was passsed, close it after that amount of time. */
  useEffect(() => {
    if (!autoCloseAfter) {
      return;
    }

    const timerId = window.setTimeout(() => {
      setOpenInternal(false);
    }, autoCloseAfter);

    /** Unmounted - cleanup. */
    return () => {
      window.clearTimeout(timerId);
    };
  }, [autoCloseAfter]);

  const handleClose = () => {
    setOpenInternal(false);
  };

  return (
    <Provider>
      <Root open={openInternal} onOpenChange={onOpenChange} delayDuration={500}>
        <Trigger asChild>{children}</Trigger>
        <Portal>
          <Content
            className="z-[100000] rounded-[0.375rem] border border-flavour-600 bg-flavour-100 p-2 shadow-md ring-2 ring-flavour-600 ring-opacity-5"
            side={side}
            sideOffset={sideOffset}
            align={align}
            alignOffset={alignOffset}
            hideWhenDetached={true}
          >
            {content}
            {closeable && (
              <button
                type="button"
                className="absolute right-1 top-1"
                aria-label={t("common:common-close")}
                onClick={handleClose}
              >
                <Cross className="size-5 shrink-0 text-brand-500" />
              </button>
            )}
            <Arrow asChild>
              <div className="absolute -top-3 left-1/2 size-3 -translate-x-1/2 translate-y-1/2 rotate-45 border-b border-r border-flavour-600 bg-flavour-100 shadow-md  ring-2 ring-flavour-600 ring-opacity-5" />
            </Arrow>
          </Content>
        </Portal>
      </Root>
    </Provider>
  );
};
